import { z } from 'zod';

/**
 * Resmi KBS Tesis Web Servisi sozlesmesi.
 *
 * Kaynak (uydurulmadi, resmi dokumandan ve canli WSDL'den alindi):
 *   - "KBS Tesis Web Servisleri Bilgi Dokumani", JGNK, 17 Temmuz 2018, Surum 2
 *   - https://vatandas.jandarma.gov.tr/KBS_Tesis_Servis/SrvShsYtkTml.svc?singleWsdl
 *
 * Baglama: BasicHttpsBinding -> SOAP 1.1 (text/xml + SOAPAction basligi)
 */

export const KBS_ENDPOINT = 'https://vatandas.jandarma.gov.tr/KBS_Tesis_Servis/SrvShsYtkTml.svc';
export const KBS_NS_TEMPURI = 'http://tempuri.org/';
export const KBS_NS_SERVIS = 'http://schemas.datacontract.org/2004/07/KBS_Tesis_Servis';
export const KBS_SOAP_ACTION_ONEK = 'http://tempuri.org/ISrvShsYtkTml/';

/** WSDL'deki 18 metottan bu uygulamanin kullandiklari. */
export const KBS_METOTLARI = [
  'MusteriKimlikNoGiris',
  'MusteriKimlikNoCikis',
  'MusteriKimlikNoGuncelle',
  'MusteriYabanciGiris',
  'MusteriYabanciCikis',
  'MusteriYabanciGuncelle',
  'ParametreListele',
] as const;
export type KbsMetot = (typeof KBS_METOTLARI)[number];

/* ------------------------------------------------------------------ enum */

/** snfEnum.KonaklayanKullanimSekli */
export const kbsKullanimSekliSchema = z.enum(['TANIMSIZ', 'KONAKLAMA', 'GUNICI', 'AFETZEDE']);
export type KbsKullanimSekli = z.infer<typeof kbsKullanimSekliSchema>;

/** snfEnum.KonaklayanCikisTurleri — SEHVEN: hatali giris duzeltmesi */
export const kbsCikisTuruSchema = z.enum(['TANIMSIZ', 'SEHVEN', 'TESISTENCIKIS']);
export type KbsCikisTuru = z.infer<typeof kbsCikisTuruSchema>;

/** snfEnum.Cinsiyet */
export const kbsCinsiyetSchema = z.enum(['TANIMSIZ', 'ERKEK', 'KADIN']);
export type KbsCinsiyet = z.infer<typeof kbsCinsiyetSchema>;

/** snfEnum.MedeniHal */
export const kbsMedeniHalSchema = z.enum(['TANIMSIZ', 'EVLI', 'BEKAR']);

/** SrvShsYtkTml.Kodlar — servis sonuc kodlari */
export const KBS_KODLARI = [
  'BILINMEYEN',
  'Basarili',
  'KayitBulunamadi',
  'YeniKayitBasarisiz',
  'GuncellemeBasarisiz',
  'SilmeBasarisiz',
  'VTHatasi',
  'GenelHata',
  'GirdiHatasi',
  'YetkiHatasi',
  'KimlikDogrulamaHatasi',
] as const;
export const kbsKoduSchema = z.enum(KBS_KODLARI);
export type KbsKodu = z.infer<typeof kbsKoduSchema>;

/** Dokumandaki sayisal karsiliklar (panelde gosterim ve loglama icin). */
export const KBS_KOD_NUMARALARI: Partial<Record<KbsKodu, number>> = {
  BILINMEYEN: 0,
  Basarili: 100,
  KayitBulunamadi: 101,
  YeniKayitBasarisiz: 102,
  GuncellemeBasarisiz: 103,
  SilmeBasarisiz: 104,
  VTHatasi: 105,
  GenelHata: 106,
  GirdiHatasi: 107,
  YetkiHatasi: 108,
};

/**
 * Hangi hatada tekrar denenir?
 *
 * Dokuman yalnizca VTHatasi icin "Denemeye devam edilmeli" diyor. Diger
 * hatalar veri veya yetki sorunudur; tekrar denemek resmi servisi bosuna
 * yorar ve sorunu cozmez - kayit panelde duzeltilmeyi bekler.
 */
export function kbsTekrarDenenmeliMi(kod: KbsKodu): boolean {
  return kod === 'VTHatasi' || kod === 'BILINMEYEN';
}

/** Resepsiyonistin anlayacagi Turkce karsiliklar. */
export const KBS_KOD_ACIKLAMALARI: Record<KbsKodu, string> = {
  BILINMEYEN: 'KBS beklenmeyen bir cevap döndü. Sistem tekrar deneyecek.',
  Basarili: 'Bildirim KBS tarafından kabul edildi.',
  KayitBulunamadi: 'KBS bu kayıtla eşleşen bir bilgi bulamadı.',
  YeniKayitBasarisiz: 'KBS kaydı oluşturamadı. Bilgileri kontrol edin.',
  GuncellemeBasarisiz: 'KBS güncelleme işlemini kabul etmedi.',
  SilmeBasarisiz: 'KBS çıkış işlemini kabul etmedi.',
  VTHatasi: 'KBS tarafında geçici bir veritabanı hatası. Sistem tekrar deneyecek.',
  GenelHata: 'KBS genel bir hata döndü.',
  GirdiHatasi:
    'Gönderilen bilgilerde tutarsızlık var. Sarı işaretli alanları kontrol edip tekrar gönderin.',
  // DIKKAT: burasi ne yapilacagini SOYLEMEZ, cunku 108 alti uc ayri sebep
  // barindirir (IP, sifre, tesis kodu). Sebebe ozel yonlendirme icin
  // `kbsYetkiSebebi` + `KBS_YETKI_YONLENDIRME` kullanilir.
  YetkiHatasi:
    'KBS yetki hatası: tesis kodu, yetkili T.C. no, web servis şifresi veya kayıtlı IP adresinden biri kabul edilmedi.',
  KimlikDogrulamaHatasi: 'Kimlik numarası Mernis doğrulamasından geçmedi.',
};

/**
 * Panelde gosterilecek hata metni.
 *
 * KBS'nin kendi metni cogu zaman iki kelimedir ("Yetkisiz IP") ve NE
 * YAPILACAGINI soylemez. Onceki davranis o kisa metni tercih ediyordu, bu
 * yuzden bizim yazdigimiz "IP adresinizi KBS arayuzunden guncelleyin"
 * aciklamasi hicbir zaman gorunmuyordu - resepsiyon "Yetkisiz IP" yazisina
 * bakip ne yapacagini bilemiyordu.
 *
 * Artik once ne yapilacagi yazilir; KBS'nin ham metni parantez icinde
 * kanit olarak kalir (destek konusurken ise yarar).
 */
/**
 * 108 (YetkiHatasi) altindaki gercek sebep.
 *
 * Tek bir kod uc ayri sorunu kapsiyor ve ikisi birbirini takip ediyor: once
 * "Yetkisiz IP", IP tanimlaninca "Şifre Hatalı". Sahada tam olarak bu yasandi
 * ve panel her ikisinde de "IP adresinizi güncelleyin" diyordu - yani ikinci
 * adimda tesisi yanlis yere gonderiyordu. Sebebi KBS'nin kendi metninden
 * ayirt ediyoruz.
 */
export type KbsYetkiSebebi = 'IP' | 'SIFRE' | 'BILINMIYOR';

export function kbsYetkiSebebi(hamMesaj?: string | null): KbsYetkiSebebi {
  const ham = hamMesaj ?? '';
  /*
   * DIKKAT - Turkce kucultme tuzagi: "IP".toLocaleLowerCase('tr') NOKTASIZ
   * "ıp" verir, dolayisiyla kucultulmus metinde "ip" aramak "Yetkisiz IP"yi
   * hicbir zaman bulmaz. Bu yuzden IP kontrolu ham metin uzerinde, harf
   * duyarsiz regexle yapilir.
   *
   * Kelime siniri de sart: "tipi", "iptal", "İpucu" eslesmemeli.
   */
  if (/(^|[^\p{L}])ip([^\p{L}]|$)/iu.test(ham)) return 'IP';
  const kucuk = ham.toLocaleLowerCase('tr');
  if (kucuk.includes('şifre') || kucuk.includes('sifre') || kucuk.includes('parola')) return 'SIFRE';
  return 'BILINMIYOR';
}

export const KBS_YETKI_YONLENDIRME: Record<KbsYetkiSebebi, string> = {
  IP: 'Çağıran IP adresi tesisin KBS kaydında tanımlı değil. KBS arayüzünde tesise bu IP adresini ekleyin.',
  SIFRE:
    'Web servis şifresi kabul edilmedi. Bu şifre KBS arayüzüne giriş şifrenizden FARKLI olabilir — ' +
    'KBS arayüzünden tesisin web servis şifresini doğrulayıp Ayarlar > KBS bölümünden güncelleyin.',
  BILINMIYOR: 'Tesis kodu, yetkili T.C. numarası, web servis şifresi ve kayıtlı IP adresini KBS arayüzünden doğrulayın.',
};

export function kbsHataMetni(kod: KbsKodu, hamMesaj?: string | null): string {
  const temiz = hamMesaj?.trim();
  const aciklama =
    kod === 'YetkiHatasi'
      ? `${KBS_KOD_ACIKLAMALARI[kod]} ${KBS_YETKI_YONLENDIRME[kbsYetkiSebebi(temiz)]}`
      : KBS_KOD_ACIKLAMALARI[kod];
  if (!temiz || temiz.length > 160 || temiz === aciklama) return aciklama;
  return `${aciklama} (KBS: ${temiz})`;
}

/* ------------------------------------------------------------- talepler */

/** Servis her cagride bu uc guvenlik parametresini ister. */
export const kbsKimlikSchema = z.object({
  /** KullaniciTC — servisi cagiran yetkilinin T.C. kimlik numarasi */
  kullaniciTc: z.string().regex(/^[1-9][0-9]{10}$/, 'Yetkili T.C. no 11 haneli olmalı'),
  /** TssKod — JGNK tarafindan tesise verilen kod */
  tesisKodu: z.string().regex(/^[0-9]{1,6}$/, 'Tesis kodu en fazla 6 haneli sayı olmalı'),
  /** Sifre — kisiye ozel web servis sifresi */
  sifre: z.string().min(1).max(50),
});
export type KbsKimlik = z.infer<typeof kbsKimlikSchema>;

/** MusteriKimlikNoGirisTalep — alan sirasi WSDL'deki gibi ALFABETIKTIR. */
export const kbsTcGirisSchema = z.object({
  GRSTRH: z.string(),
  ILERITARIHLI: z.boolean(),
  KIMLIKNO: z.string().regex(/^[1-9][0-9]{10}$/),
  KULLANIMSEKLI: kbsKullanimSekliSchema,
  ODANO: z.string().max(15).nullable(),
  PLKNO: z.string().max(20).nullable(),
  TELNO: z.string().max(80).nullable(),
  ULKKOD: z.string(),
});
export type KbsTcGiris = z.infer<typeof kbsTcGirisSchema>;

/** MusteriYabanciGirisTalep */
export const kbsYabanciGirisSchema = z.object({
  ADI: z.string().max(80),
  ANAADI: z.string().max(80).nullable(),
  BABADI: z.string().max(80).nullable(),
  BELGENO: z.string().max(50),
  CINSIYET: kbsCinsiyetSchema,
  DOGUMTARIHI: z.string(),
  GRSTRH: z.string(),
  ILERITARIHLI: z.boolean(),
  KULLANIMSEKLI: kbsKullanimSekliSchema,
  MEDENIHAL: kbsMedeniHalSchema,
  ODANO: z.string().max(15).nullable(),
  PLKNO: z.string().max(20).nullable(),
  SOYADI: z.string().max(80),
  TELNO: z.string().max(80).nullable(),
  ULKKOD: z.string(),
});
export type KbsYabanciGiris = z.infer<typeof kbsYabanciGirisSchema>;

/** MusteriKimlikNoCikisTalep */
export const kbsTcCikisSchema = z.object({
  CKSTIP: kbsCikisTuruSchema,
  CKSTRH: z.string(),
  KIMLIKNO: z.string().regex(/^[1-9][0-9]{10}$/),
});

/** MusteriYabanciCikisTalep */
export const kbsYabanciCikisSchema = z.object({
  BELGENO: z.string().max(50),
  CKSTIP: kbsCikisTuruSchema,
  CKSTRH: z.string(),
});

/* ------------------------------------------------------------- kurallar */

/** Giris bildirimi en fazla 3 gun geriye, 5 gun ileriye gonderilebilir. */
export const KBS_GERIYE_GUN_SINIRI = 3;
export const KBS_ILERI_GUN_SINIRI = 5;

/** `dd.MM.yyyy HH:mm` metnini xs:dateTime bicimine cevirir. */
export function kbsTarih(an: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${an.getUTCFullYear()}-${p(an.getUTCMonth() + 1)}-${p(an.getUTCDate())}T${p(an.getUTCHours())}:${p(an.getUTCMinutes())}:00`;
}

/** Giris tarihi servisin kabul ettigi pencerede mi? */
export function kbsTarihPenceresi(giris: Date, simdi = new Date()): {
  gecerli: boolean;
  ileriTarihli: boolean;
  sebep: string | null;
} {
  const gunFarki = Math.floor((giris.getTime() - simdi.getTime()) / 86_400_000);
  if (gunFarki < -KBS_GERIYE_GUN_SINIRI) {
    return {
      gecerli: false,
      ileriTarihli: false,
      sebep: `Giris tarihi ${KBS_GERIYE_GUN_SINIRI} günden eski. KBS geriye dönük bildirimi kabul etmiyor.`,
    };
  }
  if (gunFarki > KBS_ILERI_GUN_SINIRI) {
    return {
      gecerli: false,
      ileriTarihli: true,
      sebep: `Giris tarihi ${KBS_ILERI_GUN_SINIRI} günden ileri. Bildirim girişe yaklaşınca gönderilecek.`,
    };
  }
  return { gecerli: true, ileriTarihli: giris.getTime() > simdi.getTime(), sebep: null };
}
